import React from "react";
import { MdInfoOutline } from "react-icons/md";
import { roundToNearest } from "utils/graphs/roundNumbers";

const formatValue = (value) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0";
  }
  const rounded = roundToNearest(Math.abs(value));
  return rounded.val + rounded.suffix;
};

const formatIntegerWithCommas = (n) =>
  Math.round(Number(n) || 0).toLocaleString("en-US");

/** Tint a dark/monochrome icon toward blue-600 (for <img> sources). */
const PORTS_ICON_BLUE_FILTER =
  "invert(38%) sepia(89%) saturate(1800%) hue-rotate(198deg) brightness(96%) contrast(96%)";

/** Tint toward orange-600 (for <img> sources). */
const MARGINAL_COSTS_ICON_ORANGE_FILTER =
  "invert(48%) sepia(93%) saturate(1200%) hue-rotate(2deg) brightness(98%) contrast(96%)";

/** Tint toward green-600 (for <img> sources). */
const GHG_ICON_GREEN_FILTER =
  "invert(42%) sepia(72%) saturate(600%) hue-rotate(92deg) brightness(95%) contrast(92%)";
// const GHG_ICON_GREEN_FILTER =
//   "invert(42%) sepia(72%) saturate(600%) hue-rotate(92deg) brightness(95%) contrast(92%)";

const OVERVIEW_STAT_ITEMS = [
  {
    id: "evs",
    icon: "/icons/zevicon.svg",
    iconAlt: "EVs",
    label: "Cumulative Sum of EVs",
    getValue: ({ fleetSummary, isLoadingFleet, fleetError }) =>
      isLoadingFleet
        ? "Loading..."
        : fleetError
          ? "Unable to load"
          : (fleetSummary?.totalEVsVehicles ?? 0),
  },
  {
    id: "ports",
    icon: "/icons/portsicon.svg",
    iconAlt: "Charging ports",
    label: "Total EV Charging Ports",
    getValue: ({ fleetSummary, isLoadingFleet, fleetError }) =>
      isLoadingFleet
        ? "Loading..."
        : fleetError
          ? "Unable to load"
          : (fleetSummary?.totalPorts ?? 0),
  },
  // {
  //   id: "electrification",
  //   icon: "/icons/evCharger.svg",
  //   iconAlt: "Fleet electrification",
  //   label: "Fleet Electrification",
  //   render: () => (
  //     <div className="flex items-center gap-2">
  //       <div className="h-2 w-full max-w-[140px] overflow-hidden rounded-full bg-gray-200">
  //         <div
  //           className="h-full bg-optonyprimary"
  //           style={{ width: "30%" }}
  //         />
  //       </div>
  //       <span className="text-sm font-semibold text-gray-900">30%</span>
  //     </div>
  //   ),
  // },
  {
    id: "marginal-costs",
    icon: "/icons/capitalcostsicon.svg",
    iconAlt: "Total marginal costs",
    label: "Total Marginal Costs",
    getValue: ({ fleetSummary, isLoadingFleet, fleetError }) =>
      isLoadingFleet
        ? "Loading..."
        : fleetError
          ? "Unable to load"
          : `$${formatValue(fleetSummary?.totalMarginalCosts ?? 0)}`,
  },
  {
    id: "ghg",
    icon: "/ghgicon.svg",
    iconAlt: "GHG emissions saved",
    label: "GHG Emissions Saved",
    getValue: ({ fleetSummary, isLoadingFleet, fleetError }) =>
      isLoadingFleet ? (
        "Loading..."
      ) : fleetError ? (
        "Unable to load"
      ) : (
        <>
          {formatIntegerWithCommas(fleetSummary?.totalGHGEmissionsSaved ?? 0)}{" "}
          (MTCO<sub>2</sub>e)
        </>
      ),
  },
];

function OverviewStatItem({ item, state }) {
  const { fleetSummary, isLoadingFleet, fleetError } = state;

  const marginalCostsNum = Number(fleetSummary?.totalMarginalCosts);
  const showMarginalSavings =
    item.id === "marginal-costs" &&
    !isLoadingFleet &&
    !fleetError &&
    Number.isFinite(marginalCostsNum) &&
    marginalCostsNum > 0;

  const displayLabel = showMarginalSavings
    ? "Total Marginal Savings"
    : item.label;
  const iconAlt =
    item.id === "marginal-costs" && showMarginalSavings
      ? "Total marginal savings"
      : item.iconAlt;

  const content =
    item.render != null
      ? item.render()
      : item.getValue != null
        ? item.getValue(state)
        : item.value;
  const isNode = typeof content !== "string" && typeof content !== "number";

  let valueClassName = "text-lg font-semibold text-gray-900";
  let labelClassName = "text-xs text-gray-600";
  let iconFilter;
  if (item.id === "ports") {
    valueClassName = "text-lg font-semibold text-blue-600";
    labelClassName = "text-xs text-blue-600";
    iconFilter = PORTS_ICON_BLUE_FILTER;
  } else if (item.id === "marginal-costs") {
    valueClassName = "text-lg font-semibold text-orange-600";
    labelClassName = "text-xs text-orange-600";
    iconFilter = MARGINAL_COSTS_ICON_ORANGE_FILTER;
  } else if (item.id === "ghg") {
    valueClassName = "text-lg font-semibold text-green-600";
    labelClassName = "text-xs text-green-600";
    iconFilter = GHG_ICON_GREEN_FILTER;
  }

  return (
    <div
      className={`flex items-center gap-3 ${item.id === "electrification" ? "min-w-[200px] flex-1" : "min-w-[160px]"}`}>
      <img
        src={item.icon}
        alt={iconAlt}
        className="h-8 w-8"
        style={iconFilter ? { filter: iconFilter } : undefined}
      />
      <div className="flex w-full flex-col">
        <span className={labelClassName}>{displayLabel}</span>
        {isNode ? (
          <div className={valueClassName}>{content}</div>
        ) : (
          <span className={valueClassName}>{content}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Overview header + grid of fleet summary stat items.
 */
const OverviewStatsPanel = ({ fleetSummary, isLoadingFleet, fleetError }) => {
  const state = { fleetSummary, isLoadingFleet, fleetError };

  return (
    <div className="bg-gray-50 shadow-md rounded-2xl border p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          Overview
          <div className="relative inline-block">
            <MdInfoOutline
              className="text-gray-500 hover:text-gray-700 cursor-help peer"
              size={16}
            />
            <div className="opacity-0 peer-hover:opacity-100 transition-opacity duration-200 bg-white text-black text-sm rounded p-2 absolute z-10 left-1/2 transform -translate-x-1/2 mt-2 w-[300px] border border-gray-200 shadow-lg">
              Overview metrics assume comprehensive or highest electrification
              simulation for fleet inventory assets over time period studied.
            </div>
          </div>
        </div>
        {/* <button
          type="button"
          className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1 text-sm text-gray-600">
          Last 12 months
          <img
            src="/icons/Arrow drop down.svg"
            alt=""
            className="h-3 w-3 opacity-70"
          />
        </button> */}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-6">
        {OVERVIEW_STAT_ITEMS.map((item) => (
          <OverviewStatItem
            key={item.id}
            item={item}
            state={state}
          />
        ))}
      </div>
    </div>
  );
};

export default OverviewStatsPanel;
